import { useEffect } from 'react';
import { formatQuotaResetsAt, resourceNoun } from '../../../utils/isProjectQuotaExceeded';
import { useProjectQuota } from '../../../hooks/useProjectQuota';
import { SEEN_NUX_KEYS } from '../../../utils/users';
import { useDismissibleSidebarCard } from '../../Base/components/useDismissibleSidebarCard';
import {
  ProjectQuotaLearnMoreLink,
  ProjectQuotaUpgradeLink,
} from '../../../components/ProjectQuotaUpgradeLink';

/**
 * The one line under the input about the weekly quota: the limit message once it's used up,
 * otherwise a one-time announcement on the first visit after the quota applies.
 */
export const ProjectQuotaInputNotice = () => {
  const quota = useProjectQuota();
  const { isDismissed, markSeen } = useDismissibleSidebarCard(
    SEEN_NUX_KEYS.PROJECT_QUOTA_ANNOUNCEMENT_DISMISSED
  );
  const isAtLimit = !!quota && quota.remaining <= 0;
  const isAnnouncing = !!quota && !isAtLimit && !isDismissed;

  // Recorded as seen the first time it shows, but left up for this visit.
  useEffect(() => {
    if (isAnnouncing) markSeen();
  }, [isAnnouncing]);

  if (!isAtLimit && !isAnnouncing) return null;

  const location = isAtLimit ? 'quota_input_banner' : 'quota_announcement';

  // An announcement, not a sell: one quiet link, on the same line.
  if (isAnnouncing) {
    return (
      <p className='mt-3 text-center text-sm text-gray-600'>
        <span>
          Update: You can create up to {quota.limit} {resourceNoun(quota.limit)} per week with your
          free account.
        </span>{' '}
        <ProjectQuotaLearnMoreLink location={location} quota={quota} />
      </p>
    );
  }

  // At the limit, upgrading is the one call to action; the explainer stays secondary.
  const reset = formatQuotaResetsAt(quota.resets_at);
  return (
    <p className='mt-3 text-center text-sm text-gray-600'>
      <span>
        ⚠️ You&apos;ve reached your limit of {quota.limit} free {resourceNoun(quota.limit)} this
        week. {reset ? `Limit resets on ${reset}.` : 'Limit resets next week.'}
      </span>
      <span className='block'>
        <ProjectQuotaUpgradeLink
          location={location}
          quota={quota}
          colorClassName='text-amber-700 hover:text-amber-800'>
          Upgrade for unlimited
        </ProjectQuotaUpgradeLink>
        <span className='text-gray-400' aria-hidden='true'>
          {' · '}
        </span>
        <ProjectQuotaLearnMoreLink location={location} quota={quota} />
      </span>
    </p>
  );
};
